import { gql, useMutation, useQuery } from "@apollo/client";
import {
  WBAlert,
  WBButton,
  WBFlex,
  WBForm,
  WBS3AvatarUpload,
  WBTextField,
} from "@apptractive/design-system-web";
import {
  getNextOnboardingStep,
  Image,
  PROFILE_PLACEHOLDER,
  updateUserAttributes,
} from "@apptractive/stm-shared";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CSGetSub as GET_SUB } from "../../customGraphql/queries";
import { updateUser as UPDATE_USER } from "../../graphql/mutations";
import { getUser } from "../../graphql/queries";
import { getOnboardingPath } from "../../helpers/onboarding";

interface UserOnboardingFormData {
  firstName: string;
  lastName: string;
  about?: string;
}

const OnboardingUserForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UserOnboardingFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const [loading, setLoading] = useState(false);
  const { data: subData } = useQuery(gql(GET_SUB));
  const { data: userData } = useQuery(gql(getUser), {
    variables: {
      id: subData?.sub,
    },
    skip: !subData || !subData?.sub,
  });

  const [updateUser, { error: updateError }] = useMutation(gql(UPDATE_USER));

  const user = useMemo(() => userData?.getUser || {}, [userData]);

  const inputs = useMemo(
    () => ({
      firstName: {
        label: t("firstNameTitle", { ns: "common" }),
        name: "firstName" as "firstName",
        type: "text",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("firstNameRequired", { ns: "common" }),
          //pattern: {
          //  message: t("firstNameInvalid")
          //}
        },
      },
      lastName: {
        label: t("lastNameTitle", { ns: "common" }),
        name: "lastName" as "lastName",
        type: "text",
        placeholder: "",
        defaultValue: "",
        rules: {
          required: t("lastNameRequired", { ns: "common" }),
          //pattern: {
          //  message: t("lastNameInvalid")
          //}
        },
      },
      about: {
        label: t("aboutTitle", { ns: "common" }),
        name: "about" as "about",
        type: "text",
        placeholder: "",
        defaultValue: "",
        rules: {},
      },
    }),
    [t]
  );

  useEffect(() => {
    if (user.firstName) {
      setValue("firstName", user.firstName, { shouldValidate: true });
    }
    if (user.lastName) {
      setValue("lastName", user.lastName, { shouldValidate: true });
    }
    if (user.about) {
      setValue("about", user.about, { shouldValidate: true });
    }
  }, [user, setValue]);

  const onImageUpload = async (image: Image) => {
    try {
      await updateUser({
        variables: { input: { id: subData?.sub, profileImg: image } },
      });
    } catch (err) {
      console.log("err updating user: ", err);
    }
  };

  const onSubmit = async (data: UserOnboardingFormData) => {
    setLoading(true);
    try {
      const updateData = await updateUser({
        variables: {
          input: {
            ...data,
            onboardingStatus: getNextOnboardingStep(user),
            id: subData?.sub,
          },
        },
      });

      // update cognito attributes if required
      try {
        await updateUserAttributes(user, data);
      } catch (err) {
        console.log(err);
      }

      setLoading(false);

      const updatedUser = updateData?.data?.updateUser;
      navigate(getOnboardingPath(updatedUser), {
        replace: true,
      });
    } catch (err) {
      console.log("error updating user: ", err);
      setLoading(false);
    }
  };

  return (
    <>
      <WBFlex
        flexDirection="column"
        alignItems="center"
        width={{
          xs: "100%",
          sm: "80%",
          md: "60%",
          lg: "40%",
        }}
      >
        <WBS3AvatarUpload
          onImageUpload={onImageUpload}
          imgKey={user?.profileImg?.key}
          src={PROFILE_PLACEHOLDER.default}
          sx={{
            width: "120px",
            height: "120px",
          }}
        />
        <WBForm onSubmit={handleSubmit(onSubmit)} alignSelf="stretch">
          <Controller
            control={control}
            name={inputs.firstName.name}
            rules={inputs.firstName.rules}
            defaultValue={inputs.firstName.defaultValue}
            render={({ field }) => (
              <WBTextField
                {...field}
                type="text"
                label={inputs.firstName.label}
                //placeholder={inputs.firstName.placeholder}
                error={!!(errors.firstName && errors.firstName.message)}
                helperText={
                  (errors.firstName && errors.firstName.message) || ""
                }
                margin="dense"
              />
            )}
          />
          <Controller
            control={control}
            name={inputs.lastName.name}
            rules={inputs.lastName.rules}
            defaultValue={inputs.lastName.defaultValue}
            render={({ field }) => (
              <WBTextField
                {...field}
                type={inputs.lastName.type}
                label={inputs.lastName.label}
                //placeholder={inputs.lastName.placeholder}
                error={!!(errors.lastName && errors.lastName.message)}
                helperText={(errors.lastName && errors.lastName.message) || ""}
                margin="dense"
              />
            )}
          />
          <Controller
            control={control}
            name={inputs.about.name}
            rules={inputs.about.rules}
            defaultValue={inputs.about.defaultValue}
            render={({ field }) => (
              <WBTextField
                {...field}
                type={inputs.about.type}
                label={inputs.about.label}
                multiline
                rows={4}
                //placeholder={inputs.about.placeholder}
                error={!!(errors.about && errors.about.message)}
                helperText={(errors.about && errors.about.message) || ""}
                margin="dense"
              />
            )}
          />

          <WBButton sx={{ mt: 1 }} loading={loading}>
            {t("nextTitle", { ns: "common" })}
          </WBButton>
        </WBForm>
        {updateError?.message && (
          <WBAlert
            title={updateError.message}
            severity="error"
            sx={{ my: 2 }}
          />
        )}
      </WBFlex>
    </>
  );
};

export default OnboardingUserForm;
